import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { getFirebaseDb } from "@/lib/firebase-client";
import type { StorefrontSiteCode } from "@/lib/storefront-site";

/** One row of the customer's order history, read from Firestore. */
export type CustomerOrderSummary = {
  orderToken: string;
  invoiceNumber: string;
  placedAt: string | null;
  siteCode: string;
  status: string;
  total: number;
  currency: string;
  itemCount: number;
  primaryItemName: string | null;
};

/** Full order detail, fetched live from the CMS (Snipcart admin API). */
export type CustomerOrderDetail = {
  token: string;
  invoiceNumber: string;
  placedOn: string | null;
  displayStatusLabel: string;
  paymentStatus: string;
  currency: string;
  /** Which storefront the order was placed on (`asorockfood` / `asorockfrozen`).
   *  The detail page guards against showing an order from a different
   *  storefront — see /account/orders/[token].tsx. */
  siteCode: string;
  fulfilmentMode: string;
  shippingMethod: string;
  trackingUrl: string | null;
  trackingNumber: string | null;
  /** How the delivery is being fulfilled: "stuart" (live tracking map via
   *  trackingUrl), "internal" (one of our own drivers — see driverName /
   *  driverPhone), or "none" (not dispatched yet). */
  deliveryKind: string;
  driverName: string | null;
  driverPhone: string | null;
  /** Latest dropoff ETA from the Stuart status webhook (ISO-8601 string), display-only.
   *  Null until/unless a courier ETA is known. */
  eta: string | null;
  totals: {
    subtotal: number;
    fulfilmentCost: number;
    taxTotal: number;
    discountTotal: number;
    refundedTotal: number;
    finalTotal: number;
  };
  items: Array<{
    name: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    modifierGroups: Array<{
      name: string;
      selections: Array<{ label: string; priceAdjustment: number | null }>;
    }>;
  }>;
  canReorder: boolean;
  reorderUnavailableReason: string | null;
};

/** One pre-built cart line returned by the CMS reorder endpoint. Matches the
 *  `StorefrontCheckoutSessionLineDto` shape on the server; only the fields
 *  the web reorder page consumes are declared here. */
export type ReorderLine = {
  lineId: string;
  quantity: number;
  product: {
    itemId: string;
    name: string;
    price: number | string;
    url: string;
    description?: string;
    imageUrl?: string;
    buttonAttributes?: Record<string, string>;
    customFields?: Array<{
      name: string;
      type?: string;
      required?: boolean;
      value?: string;
      optionsText?: string;
    }>;
  };
};

export type ReorderResult = {
  canReorder: boolean;
  reason: string | null;
  /** Pre-built cart lines (the items the customer originally bought,
   *  revalidated against the current menu). Present when canReorder is true. */
  lines: ReorderLine[] | null;
  /** Flutter-WebView session URL. Web no longer uses this; we rebuild the
   *  customer's cart client-side from `lines` instead. */
  session: { checkoutUrl?: string } | null;
};

function toIsoString(value: unknown): string | null {
  if (
    value &&
    typeof value === "object" &&
    typeof (value as { toDate?: () => Date }).toDate === "function"
  ) {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  return typeof value === "string" ? value : null;
}

/**
 * Lists the customer's orders for a single storefront, newest-first, straight
 * from Firestore. A customer can sign into the same Firebase account on
 * either storefront (Fresh / Frozen) but their order history is scoped to
 * the storefront the order was placed on — so this call always returns
 * orders for `siteCode` only.
 *
 * Requires the composite index declared in
 * `infra/firebase/firestore.indexes.json`
 * (`orders` collection: `siteCode ASC, placedAt DESC`).
 */
export async function listCustomerOrders(
  uid: string,
  siteCode: StorefrontSiteCode,
): Promise<CustomerOrderSummary[]> {
  const ordersRef = collection(getFirebaseDb(), "customers", uid, "orders");
  const snapshot = await getDocs(
    query(
      ordersRef,
      where("siteCode", "==", siteCode),
      orderBy("placedAt", "desc"),
    ),
  );
  return snapshot.docs.map((document) => {
    const data = document.data();
    return {
      orderToken: String(data.orderToken ?? document.id),
      invoiceNumber: String(data.invoiceNumber ?? ""),
      placedAt: toIsoString(data.placedAt),
      siteCode: String(data.siteCode ?? ""),
      status: String(data.status ?? ""),
      total: typeof data.total === "number" ? data.total : 0,
      currency: String(data.currency ?? "GBP"),
      itemCount: typeof data.itemCount === "number" ? data.itemCount : 0,
      primaryItemName:
        typeof data.primaryItemName === "string" ? data.primaryItemName : null,
    };
  });
}

/** Fetches full order detail via the CMS proxy (needs a Firebase ID token). */
export async function fetchCustomerOrderDetail(
  orderToken: string,
  idToken: string,
): Promise<CustomerOrderDetail | null> {
  const response = await fetch(
    `/api/app/customers/orders/${encodeURIComponent(orderToken)}`,
    {
      headers: { Authorization: `Bearer ${idToken}`, Accept: "application/json" },
      cache: "no-store",
    },
  );
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`Could not load the order (HTTP ${response.status}).`);
  }
  return (await response.json()) as CustomerOrderDetail;
}

/** Asks the CMS to revalidate a past order and mint a fresh checkout session. */
export async function requestReorder(
  orderToken: string,
  idToken: string,
): Promise<ReorderResult> {
  const response = await fetch(
    `/api/app/customers/orders/${encodeURIComponent(orderToken)}/reorder`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${idToken}`, Accept: "application/json" },
      cache: "no-store",
    },
  );
  if (!response.ok) {
    throw new Error(`Reorder failed (HTTP ${response.status}).`);
  }
  return (await response.json()) as ReorderResult;
}
